import React, { useState, useEffect } from 'react';
import { Shield, FileText, Gavel, Scale, AlertCircle } from 'lucide-react';
import { useTenant } from '../contexts/TenantContext';
import { MarketingLayout } from '../components/shell/MarketingLayout';
import { LegalHero } from '../components/shell/LegalHero';
import { SEO } from '../components/SEO';
import { cn } from '../lib/utils';

export const DmcaPage = () => {
  const { brand, colorMode, activeBrandId } = useTenant();
  const theme = activeBrandId;
  const isDark = colorMode === 'dark';
  const brandName = brand?.name || 'Our Platform';

  const proseClasses = cn(
    "prose prose-lg max-w-none leading-relaxed",
    "prose-headings:font-black prose-headings:tracking-tight prose-p:text-black prose-li:text-black prose-strong:text-black dark:prose-headings:text-white dark:prose-p:text-white dark:prose-li:text-white dark:prose-strong:text-white"
  );

  const [activeSection, setActiveSection] = useState('protection');

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['protection', 'takedown', 'repeat', 'counter'];
      let current = sections[0];
      for (const section of sections) {
        const el = document.getElementById(section);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 150) {
            current = section;
          }
        }
      }
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 50) {
        current = sections[sections.length - 1];
      }
      setActiveSection(current);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { id: 'protection', label: '1. Copyright Protection' },
    { id: 'takedown', label: '2. Takedown Notice' },
    { id: 'repeat', label: '3. Repeat Infringers' },
    { id: 'counter', label: '4. Counter-Notice' },
  ];

  return (
    <MarketingLayout mainClassName="pt-28 pb-24 px-4 sm:px-6 lg:px-8">
      <SEO title="DMCA Policy" description={`Read the Copyright and DMCA Policy for ${brandName}.`} />
        <div className="max-w-6xl mx-auto">
          
          <LegalHero 
            title="DMCA Policy"
            subtitle="Our policy regarding intellectual property rights and the Digital Millennium Copyright Act."
            icon={<Scale size={14} />}
            label="Intellectual Property"
            version="1.0"
            updated="June 2026"
          />

          <div className="flex flex-col md:flex-row gap-12 lg:gap-24 relative">
            
            <aside className="md:w-64 flex-shrink-0 hidden md:block">
              <div className="sticky top-32 space-y-6">
                <h4 className={cn("font-bold uppercase text-[10px] tracking-widest font-mono", isDark ? "text-slate-500" : "text-slate-400")}>
                  On this page
                </h4>
                <nav className="flex flex-col gap-3 relative before:absolute before:inset-y-0 before:left-0 before:w-px before:bg-slate-200 dark:before:bg-slate-800">
                  {navItems.map((item) => (
                    <a 
                      key={item.id}
                      href={`#${item.id}`} 
                      className={cn(
                        "pl-4 py-1 text-sm font-medium transition-all duration-200 relative",
                        activeSection === item.id 
                          ? "text-brand-primary before:absolute before:inset-y-0 before:-left-px before:w-[2px] before:bg-brand-primary" 
                          : isDark ? "text-slate-400 hover:text-slate-200" : "text-slate-500 hover:text-slate-800"
                      )}
                    >
                      {item.label}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>

            <div className="flex-1 max-w-3xl space-y-20">
              
              <section id="protection" className="scroll-mt-32">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-brand-primary/10 flex items-center justify-center text-brand-primary">
                    <Shield size={24} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold font-display">1. Copyright Protection</h2>
                </div>
                <div className={proseClasses}>
                  <p>
                    {brandName} respects the intellectual property of others and asks that users of our platform do the same. In connection with our platform, especially the Digital Vault, we have adopted and implemented a policy respecting copyright law that provides for the removal of any infringing materials.
                  </p>
                </div>
              </section>

              <section id="takedown" className="scroll-mt-32">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-blue-500/10 flex items-center justify-center text-blue-500">
                    <AlertCircle size={24} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold font-display">2. Filing a Takedown Notice</h2>
                </div>
                <div className={proseClasses}>
                  <p>
                    If you believe that one of our users is, through the use of our platform, unlawfully infringing the copyright(s) in a work, and wish to have the allegedly infringing material removed, the following information in the form of a written notification (pursuant to 17 U.S.C. § 512(c)) must be provided to our designated Copyright Agent:
                  </p>
                  <ul className="list-disc pl-5 mt-4 marker:text-blue-500">
                    <li>Your physical or electronic signature.</li>
                    <li>Identification of the copyrighted work(s) that you claim to have been infringed.</li>
                    <li>Identification of the material on our services that you claim is infringing and that you request us to remove.</li>
                    <li>Sufficient information to permit us to locate such material (such as a URL or Digital Vault path).</li>
                    <li>Your address, telephone number, and e-mail address.</li>
                    <li>A statement that you have a good faith belief that use of the objectionable material is not authorized by the copyright owner.</li>
                    <li>A statement that the information in the notification is accurate, and under penalty of perjury, that you are either the owner of the copyright that has allegedly been infringed or that you are authorized to act on behalf of the copyright owner.</li>
                  </ul>
                </div>
              </section>

              <section id="repeat" className="scroll-mt-32">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-rose-500/10 flex items-center justify-center text-rose-500">
                    <Gavel size={24} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold font-display">3. Repeat Infringers</h2>
                </div>
                <div className={proseClasses}>
                  <p>
                    Under our Acceptable Use Policy and DMCA obligations, {brandName} will terminate the accounts of users who are repeat infringers. We may also, at our sole discretion, limit access to the platform or terminate the accounts of any users who infringe any intellectual property rights of others, whether or not there is any repeat infringement.
                  </p>
                </div>
              </section>

              <section id="counter" className="scroll-mt-32">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-teal-500/10 flex items-center justify-center text-teal-500">
                    <FileText size={24} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold font-display">4. Counter-Notice</h2>
                </div>
                <div className={proseClasses}>
                  <p>
                    If you believe that the materials you uploaded were removed by mistake or misidentification, you may file a counter-notice. Your counter-notice must include your signature, identification of the removed material, a statement under penalty of perjury that you believe the removal was a mistake, and your contact information.
                  </p>
                </div>
              </section>

            </div>
          </div>
        </div>
    </MarketingLayout>
  );
};
